import React, { useEffect, useState, useContext } from "react";
import PropTypes from 'prop-types';

import { useNavigate } from 'react-router-dom';

import { Alert, AlertTitle, Box, Button, CircularProgress, Stack, Typography } from '@mui/material';

import { EntityContext } from '../../entity';
import { AccountContext } from '../../account';
import { prepareSignature } from '../../utils/prepareSignature';

function SignatureButton(props) {

    const { sendSignature, sendBatchSignature, sendBatchEndorsementSignature, loading } = useContext(EntityContext)
    const { getSession } = useContext(AccountContext)

  const [fingerprint, setFingerprint] = useState(null);
  const [ipData, setIpData] = useState(null);
  const [prepareError, setPrepareError] = useState(null);
  const [attempt, setAttempt] = useState(0);
  const [token, setToken] = useState(null)

  useEffect(() => {
    getSession()
        .then((data) => {
            setToken(data.idToken.jwtToken)
        })
    }, [])

  const navigate = useNavigate();

  const navigateToSignatureFinished = () => {
    navigate(`/signature/finish`);
  };

  useEffect(() => {
    let cancelled = false;

    setPrepareError(null);
    setIpData(null);
    setFingerprint(null);

    prepareSignature()
      .then(({ ip, fingerprint: fp }) => {
        if (cancelled) return;
        setIpData(ip);
        setFingerprint(fp);
      })
      .catch((error) => {
        if (cancelled) return;
        const code = error.code || 'PREPARE_FAILED';
        console.error('Falha ao preparar assinatura:', code, error.details || error);
        setPrepareError(code);
      });

    return () => {
      cancelled = true;
    };
  }, [attempt]);


    const handleSubmission = async () => {
        if (!ipData || !fingerprint) return;

        try {
            let signed;
            if (props.signerKey) {
                await sendSignature(props.otp, props.signerKey, ipData, fingerprint)
            } else if (props.isEndorsement) {
                signed = await sendBatchEndorsementSignature(props.batchKey, props.otp, ipData, fingerprint, ...[props.beginTrace?.()].filter(Boolean))
            } else {
                signed = await sendBatchSignature(token, props.otp, props.batchKey, ipData, fingerprint)
            }
            if (props.onSigned?.(signed)) return
            navigateToSignatureFinished()
        } catch (error) {
            props.onError?.(error)
        }
    }

    const isPreparing = !prepareError && (!ipData || !fingerprint);
    const showSpinner = isPreparing || loading;

    return (
        <Box
            sx={{
                width: { xs: '100%', sm: 440 },
                maxWidth: '100%',
                alignSelf: { xs: 'stretch', sm: 'center' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
            }}
        >
            {prepareError ? (
                <Alert severity="warning" sx={{ width: '100%', alignItems: 'flex-start' }}>
                    <AlertTitle sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>
                        Não foi possível preparar a assinatura
                    </AlertTitle>
                    <Typography sx={{ fontSize: { xs: '0.875rem', sm: '1rem' }, mb: 1 }}>
                        A página não conseguiu identificar este aparelho a partir deste navegador. Por isso o botão de assinar não foi liberado.
                    </Typography>
                    <Box
                        component="ul"
                        sx={{
                            pl: 2.5,
                            my: 1,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                            '& li': { mb: 0.5 },
                        }}
                    >
                        <li>Abra o link no Chrome ou no Safari, fora do WhatsApp.</li>
                        <li>Desative bloqueador de anúncios, VPN ou antivírus só neste site e atualize a página.</li>
                        <li>Confira se há internet e se o JavaScript está permitido.</li>
                        <li>Se estiver no Wi-Fi, tente os dados móveis.</li>
                    </Box>
                    <Stack
                        direction={{ xs: 'column', sm: 'row' }}
                        spacing={1.5}
                        alignItems={{ xs: 'stretch', sm: 'center' }}
                        justifyContent="space-between"
                        sx={{ mt: 1.5 }}
                    >
                        <Typography
                            component="p"
                            sx={{
                                fontFamily: 'monospace',
                                fontSize: { xs: '0.75rem', sm: '0.8125rem' },
                                wordBreak: 'break-all',
                                m: 0,
                            }}
                        >
                            Código: {prepareError}
                        </Typography>
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={() => setAttempt((value) => value + 1)}
                            sx={{ width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}
                        >
                            Tentar novamente
                        </Button>
                    </Stack>
                </Alert>
            ) : (
                <Button
                    variant="contained"
                    onClick={() => handleSubmission()}
                    disabled={isPreparing || loading}
                    startIcon={showSpinner ? <CircularProgress size={20} color="inherit" /> : null}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                >
                    {isPreparing ? 'Preparando...' : loading ? 'Processando...' : 'Assinar'}
                </Button>
            )}
        </Box>
    );
}

SignatureButton.propTypes = {
    otp: PropTypes.string,
    signerKey: PropTypes.string,
    batchKey: PropTypes.string,
    isEndorsement: PropTypes.bool,
    onError: PropTypes.func,
    beginTrace: PropTypes.func,
    onSigned: PropTypes.func,
};

export default SignatureButton;
